import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api/api";
import { logClientError } from "../api/errorLogger";

const TICKET_TYPES = ["BUG", "ENHANCEMENT", "UPDATE_REQUEST", "OTHER"];

const formatLabel = (value) =>
  value
    ? value
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "-";

const formatDate = (date) =>
  date
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(date))
    : "-";

export default function Home() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const loadOverview = useCallback(async () => {
    setError("");
    setIsLoading(true);

    try {
      const data = await apiFetch("/dashboard/home");
      setOverview(data);
      setLastUpdatedAt(new Date());
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Home", action: "loadOverview" });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const mailboxes = overview?.mailboxes;
  const tenants = overview?.tenants;
  const mailActivity = overview?.mailActivity?.recent || [];
  const tickets = overview?.tickets;
  const adminActivity = overview?.adminActivity?.recent || [];
  const isMissingExpandedData =
    overview && (!overview.tenants || !overview.mailActivity || !overview.tickets || !overview.adminActivity);
  const capacityUsed = mailboxes?.totalCapacity
    ? Math.min(100, Math.round(((mailboxes.assigned || 0) / mailboxes.totalCapacity) * 100))
    : 0;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="admin-page-eyebrow">Admin dashboard</p>
          <h2>Overview</h2>
        </div>

        <div className="admin-page-header-actions">
          {lastUpdatedAt && (
            <span>Updated {formatDate(lastUpdatedAt)}</span>
          )}
          <button
            className="admin-button admin-button-secondary"
            type="button"
            onClick={loadOverview}
            disabled={isLoading}
          >
            Refresh
          </button>
        </div>
      </div>

      <section className="admin-quick-actions" aria-label="Quick actions">
        <button
          className="admin-button admin-button-primary"
          type="button"
          onClick={() => navigate("/dashboard/mailboxes", { state: { action: "add" } })}
        >
          Add Mailbox
        </button>
        <button
          className="admin-button admin-button-primary"
          type="button"
          onClick={() => navigate("/dashboard/tenants", { state: { action: "add" } })}
        >
          Add Tenant
        </button>
        <button
          className="admin-button admin-button-secondary"
          type="button"
          onClick={() => navigate("/dashboard/tickets")}
        >
          Create Ticket
        </button>
        <button
          className="admin-button admin-button-secondary"
          type="button"
          onClick={() => navigate("/dashboard/mailboxes", { state: { filter: "withMail" } })}
        >
          View Mailboxes With Mail
        </button>
      </section>

      {error && <p className="admin-empty-state admin-error-state">{error}</p>}
      {isMissingExpandedData && (
        <p className="admin-empty-state admin-notice-state">
          Dashboard overview is missing the latest summary fields. Restart the backend server if it is still running an older build.
        </p>
      )}

      {isLoading ? (
        <p className="admin-empty-state">Loading dashboard overview...</p>
      ) : (
        <>
          {mailboxes?.totalCapacity === null && (
            <div className="admin-empty-state admin-notice-state">
              <span>Total mailbox capacity has not been configured yet.</span>
              <button
                className="admin-button admin-button-secondary"
                type="button"
                onClick={() => navigate("/dashboard/settings")}
              >
                Open Settings
              </button>
            </div>
          )}

          <section className="admin-section" aria-labelledby="mailbox-overview-heading">
            <div className="admin-section-heading">
              <div>
                <h3 id="mailbox-overview-heading">Mailbox overview</h3>
                <p>Capacity, availability, and current mail status.</p>
              </div>
            </div>
            <div className="admin-overview-grid">
              <MetricCard icon="capacity" label="Total Capacity" value={mailboxes?.totalCapacity ?? "Not set"} meta={`${mailboxes?.created ?? 0} mailboxes created`} />
              <MetricCard icon="assigned" label="Assigned" value={mailboxes?.assigned ?? 0}>
                <div className="admin-progress">
                  <div className="admin-progress-track" aria-hidden="true">
                    <span className="admin-progress-bar" style={{ width: `${capacityUsed}%` }} />
                  </div>
                  <small>{mailboxes?.totalCapacity ? `${capacityUsed}% of capacity` : "Set capacity to track usage"}</small>
                </div>
              </MetricCard>
              <MetricCard icon="available" label="Available" value={mailboxes?.available ?? "Not set"} meta="Ready to be assigned" />
              <MetricCard icon="mail" label="With Mail" value={mailboxes?.withMail ?? 0} meta="Awaiting tenant pickup" tone="accent" />
              <MetricCard icon="created" label="Created" value={mailboxes?.created ?? 0} meta="Mailbox records in the system" />
              <MetricCard icon="service" label="Out Of Service" value={mailboxes?.outOfService ?? 0} meta="Requires attention" tone="danger" />
            </div>
          </section>

          <section className="admin-section" aria-labelledby="tenant-overview-heading">
            <div className="admin-section-heading">
              <div>
                <h3 id="tenant-overview-heading">Tenant overview</h3>
                <p>Current occupancy and assignment status.</p>
              </div>
            </div>
            <div className="admin-overview-grid admin-overview-grid-tenant">
              <MetricCard icon="users" label="Active Tenants" value={tenants?.active ?? 0} meta="Currently active" />
              <MetricCard icon="pending" label="Pending Tenants" value={tenants?.pending ?? 0} meta="Waiting for activation" tone="accent" />
              <MetricCard icon="moved" label="Moved Out" value={tenants?.movedOut ?? 0} meta="Historical tenant records" />
              <MetricCard icon="unassigned" label="Without Mailbox" value={tenants?.withoutMailbox ?? 0} meta="Needs an assignment" tone="danger" />
            </div>
          </section>

          <section className="admin-section" aria-labelledby="activity-heading">
            <div className="admin-section-heading">
              <div>
                <h3 id="activity-heading">Recent activity</h3>
                <p>Latest mail events, tickets, and administrative changes.</p>
              </div>
            </div>
            <div className="admin-dashboard-panels">
            <article className="admin-dashboard-panel admin-panel-mail">
              <div className="admin-panel-header">
                <h3>Recent Mail Activity</h3>
              </div>

              {!mailActivity.length ? (
                <p className="admin-panel-empty">No recent mail activity.</p>
              ) : (
                <ul className="admin-activity-list">
                  {mailActivity.map((event) => (
                    <li key={event.id}>
                      <div>
                        <strong>{formatLabel(event.type)}</strong>
                        <span>
                          {event.mailbox?.number ? `Mailbox #${event.mailbox.number}` : "No mailbox"}
                          {event.tenant?.name ? ` · ${event.tenant.name}` : ""}
                        </span>
                      </div>
                      <time>{formatDate(event.createdAt)}</time>
                    </li>
                  ))}
                </ul>
              )}
            </article>

            <article className="admin-dashboard-panel admin-panel-tickets">
              <div className="admin-panel-header">
                <h3>Open Tickets</h3>
                <strong>{tickets?.open ?? 0}</strong>
              </div>

              <div className="admin-ticket-breakdown">
                {TICKET_TYPES.map((type) => (
                  <span key={type}>
                    {formatLabel(type)}: {tickets?.byType?.[type] ?? 0}
                  </span>
                ))}
              </div>

              {!tickets?.newest?.length ? (
                <p className="admin-panel-empty">No open tickets.</p>
              ) : (
                <ul className="admin-activity-list">
                  {tickets.newest.map((ticket) => (
                    <li key={ticket.id}>
                      <div>
                        <strong>{ticket.title}</strong>
                        <span>{formatLabel(ticket.type)}</span>
                      </div>
                      <time>{formatDate(ticket.createdAt)}</time>
                    </li>
                  ))}
                </ul>
              )}
            </article>

            <article className="admin-dashboard-panel admin-panel-admin">
              <div className="admin-panel-header">
                <h3>Recent Admin Activity</h3>
              </div>

              {!adminActivity.length ? (
                <p className="admin-panel-empty">No recent admin activity.</p>
              ) : (
                <ul className="admin-activity-list">
                  {adminActivity.map((activity) => (
                    <li key={activity.id}>
                      <div>
                        <strong>{formatLabel(activity.action)}</strong>
                        <span>
                          {activity.entityType}
                          {activity.entityId ? ` #${activity.entityId}` : ""}
                          {activity.actorAdmin?.email ? ` · ${activity.actorAdmin.email}` : ""}
                        </span>
                      </div>
                      <time>{formatDate(activity.createdAt)}</time>
                    </li>
                  ))}
                </ul>
              )}
            </article>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, meta, tone, children }) {
  return (
    <div className={`admin-overview-card${tone ? ` is-${tone}` : ""}`}>
      <span className="admin-metric-icon">
        <MetricIcon name={icon} />
      </span>
      <span>{label}</span>
      <strong>{value}</strong>
      {children || <p className="admin-metric-meta">{meta}</p>}
    </div>
  );
}

function MetricIcon({ name }) {
  const paths = {
    capacity: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 8h10M7 12h10M7 16h6" /></>,
    assigned: <><path d="M20 7 10 17l-5-5" /><circle cx="12" cy="12" r="10" /></>,
    available: <><path d="M12 5v14M5 12h14" /><circle cx="12" cy="12" r="10" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    created: <><path d="M4 20V9a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v11" /><path d="M2 20h20M12 4v10M12 8h5" /></>,
    service: <><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>,
    pending: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    moved: <><path d="M10 17l5-5-5-5M15 12H3" /><path d="M15 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" /></>,
    unassigned: <><circle cx="9" cy="8" r="4" /><path d="M2 21v-2a5 5 0 0 1 5-5h4a5 5 0 0 1 4.6 3M17 11l5 5M22 11l-5 5" /></>,
  };

  return (
    <svg className="admin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}
